import { observable, action, reaction } from "mobx";
import omit from "lodash/omit";
import pick from "lodash/pick";
import { ApiService, ApiUrl } from "helpers";
import { loadAutomationIntoCanvas, BuildArrayStructure, BuildTriggerList, BuildCanvasJson } from "components/Rappid/RappidCanvasFunctions";
import rappidStore from "./rappidStore";
import automationStore from "./automationStore";
import { AutomationType, AutomationLoopingType, AutomationTypeText, TriggerType, TriggerTypeClient } from "helpers/enum";
import { HeaderTabName } from "helpers/Constants";
import Alert from "react-s-alert";

const UNREACHABLE = "Roi could not be reached. Please try again.";

// The campaign settings sent with every message, by the names the builder saves them under (ROI-412).
// The steps and triggers are added to them in canvasSnapshot().
const CANVAS_FIELDS = [
  "name", "description", "audiences", "users", "groups", "offices", "includeRecentNoteOwner",
  "isAutomationIndefinite", "loopingType",
  "isAutomationReEntryRestriction", "automationReEntryRestrictionWeek", "automationReEntryRestrictionDay", "automationReEntryRestrictionHour",
  "isAutomationLooping", "automationLoopingWeek", "automationLoopingDay", "automationLoopingHour",
  "category", "isSpecificTimezone", "specificDays", "startHour", "endHour", "timezone",
];

// Worked out again by the builder from a delay's own fields, such as on every return to the Builder
// tab, so a change to only these is not the recruiter changing the campaign (as in canvasMarks.js).
const DERIVED_ROI_DATA = ["delayDate", "previousDelayId", "previousTotalMinutesTime"];

// The send window, which Roi reads but never draws, so a change to it while Roi works is not lost.
const SEND_WINDOW_FIELDS = ["isSpecificTimezone", "specificDays", "startHour", "endHour", "timezone"];

const ID_LIST_FIELDS = ["audiences", "users", "groups", "offices"];
const byId = (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true });

// A trigger row's own column. A save writes 13 for some custom fields and the builder then reloads
// the saved list, but the column stays at the front of customFieldTriggerId ("72-5").
const ownColumn = row => {
  const firstColumn = row && row.firstColumn;
  const prefix = firstColumn && typeof firstColumn.customFieldTriggerId === "string"
    ? Number(firstColumn.customFieldTriggerId.split("-")[0])
    : NaN;
  return Number.isNaN(prefix) ? row : { ...row, firstColumn: { ...firstColumn, id: prefix } };
};

/**
 * The canvas as the checks for changes compare it: as sent, less what changes without the recruiter
 * changing anything. That is the delay fields above, and what a save reloads from the server in its
 * own form: each trigger row's column (see ownColumn) and the order of the id lists. The send window
 * is left out of the check made while Roi works (DURING_TURN), since a reply never draws it.
 */
const editKey = (canvas, { withSendWindow = true } = {}) => {
  if (!canvas) return null;
  const steps = JSON.parse(canvas.arrayStructure).map(step =>
    step.roiData && !Array.isArray(step.roiData) ? { ...step, roiData: omit(step.roiData, DERIVED_ROI_DATA) } : step
  );
  const triggers = JSON.parse(canvas.triggerList || "[]").map(ownColumn);
  const idLists = ID_LIST_FIELDS.reduce(
    (lists, field) => ({ ...lists, [field]: Array.isArray(canvas[field]) ? [...canvas[field]].sort(byId) : canvas[field] }),
    {}
  );
  const key = { ...canvas, ...idLists, arrayStructure: steps, triggerList: triggers };
  return JSON.stringify(withSendWindow ? key : omit(key, SEND_WINDOW_FIELDS));
};

// What the check made while Roi works compares (see editKey).
const DURING_TURN = { withSendWindow: false };

// The server turns a turn down in its own words when the campaign is too large (400), is no longer a
// draft, belongs to another conversation or to the other builder (409), has gone (404), or the
// recruiter has sent too many messages in the last minute (429) (ROI-412). Read from the HTTP
// status, since a gateway's error page is a plain string too.
//   - 429 comes from the rate limiter, outside AutoWrapper, as a plain string. Its own wording is
//     used when the body is empty or not a string.
//   - The others are in the AutoWrapper body: under error.message as MapResponseObject maps it, or
//     responseException.exceptionMessage without the map. A 400 is also what the controller returns
//     for any fault, with its generic gremlin text, which is not shown in place of ours.
const REFUSAL_STATUSES = [400, 404, 409];
const GENERIC_FAULT = "Oops, sorry, it looks like there is a gremlin in the system";
const RATE_LIMITED = "Roi has had too many messages in the last minute. Wait a few minutes and try again.";
const refusalMessage = error => {
  const response = error && error.response;
  if (!response) return null;
  const body = response.data;

  if (response.status === 429) return typeof body === "string" && body.trim() ? body.trim() : RATE_LIMITED;
  if (!body || !REFUSAL_STATUSES.includes(response.status)) return null;

  const text = (body.error && body.error.message) || (body.responseException && body.responseException.exceptionMessage);
  if (typeof text !== "string" || !text.trim() || text.trim().startsWith(GENERIC_FAULT)) return null;
  return text.trim();
};

// The plan names the campaign's kind of trigger; the builder numbers it. Read from the plan rather than
// inferred from the first trigger, which a trigger the inference does not list gets wrong (ROI-412).
const PlanTriggerTypes = new Map([
  ["candidate_based", TriggerType.CandidateBased],
  ["contact_based", TriggerTypeClient.ClientBased],
  ["placement_based", TriggerType.PlacementBased],
  ["job_based", TriggerType.JobBased],
]);

// Older responses have neither field, and either can be null; both read as nothing to mark. Ids are
// kept as strings, since that is what the canvas cells use.
const readFocusStepIds = result =>
  (Array.isArray(result.focusStepIds) ? result.focusStepIds : [])
    .filter(id => id !== null && id !== undefined && id !== "")
    .map(String);

const readActionSteps = result =>
  (Array.isArray(result.actionSteps) ? result.actionSteps : [])
    .filter(step => step && step.stepId !== null && step.stepId !== undefined && step.stepId !== "")
    .map(step => ({ stepId: String(step.stepId), stepKey: step.stepKey || "", label: step.label || "" }));

// The parts of the canvas Roi could not read, so will not draw over (ROI-412). Missing from older
// responses, and empty when there are none.
const readCanvasIssues = result =>
  (Array.isArray(result.canvasIssues) ? result.canvasIssues : [])
    .filter(issue => issue && (issue.where || issue.what))
    .map(issue => ({
      part: issue.part || "",
      stepId: issue.stepId === null || issue.stepId === undefined || issue.stepId === "" ? null : String(issue.stepId),
      where: issue.where || "",
      what: issue.what || "",
    }));

// The question card (ROI-417): null when Roi is not asking anything.
const readCard = result =>
  result && result.card && Array.isArray(result.card.tabs) && result.card.tabs.length > 0 ? result.card : null;

// Worth a check: what Roi applied that they might want different, each with the tab Change reopens.
const readWorthACheck = result =>
  (Array.isArray(result.worthACheck) ? result.worthACheck : [])
    .filter(item => item && item.text)
    .map(item => ({ id: String(item.id || ""), text: item.text, tabId: item.tabId || "" }));

// Before you activate: the steps that still need something, ticked off as they are done.
const readBeforeYouActivate = result =>
  (Array.isArray(result.beforeYouActivate) ? result.beforeYouActivate : [])
    .filter(item => item && item.stepId !== null && item.stepId !== undefined && item.stepId !== "")
    .map(item => ({
      stepId: String(item.stepId),
      stepKey: item.stepKey || "",
      stepName: item.stepName || "",
      label: item.label || "",
      done: !!item.done,
    }));

const readPendingStepIds = result =>
  (Array.isArray(result.pendingStepIds) ? result.pendingStepIds : [])
    .filter(id => id !== null && id !== undefined && id !== "")
    .map(String);

// The steps marked on the canvas: what Before you activate has not ticked off.
const stillToDo = items => items.filter(item => !item.done).map(({ stepId, stepKey, label }) => ({ stepId, stepKey, label }));

// The labels of the tabs Change can reopen, as the server writes the message ("Change: Timing gaps").
const CHANGE_TAB_LABELS = {
  audience: "Audience",
  runMode: "Run once or keep running",
  trigger: "Trigger",
  steps: "Steps",
  timing: "Timing gaps",
  conditions: "Conditions",
};

// What a start over leaves on the Finalise page when the new campaign does not set it: the builder's own.
const DEFAULT_FINALISE = { keepRunning: false, reEntry: false, looping: false, tags: [] };

const LATER = "later";

/**
 * A card answer as the conversation shows it, one "Tab: answer" line per tab answered in the card's
 * order. The server writes the same lines from the answer; this is what the recruiter sees at once.
 */
export const formatAnswer = (card, tabs) =>
  (card ? card.tabs : [])
    .map(tab => {
      const given = (tabs || []).find(t => t && t.tabId === tab.id);
      if (!given) return null;
      const ids = given.optionIds || [];
      if (ids.includes(LATER)) return `${tab.label}: Select this later`;
      const parts = ids.map(id => (tab.options.find(o => o.id === id) || {}).label).filter(Boolean);
      const own = (given.text || "").replace(/\s+/g, " ").trim();
      if (own) parts.push(own);
      return parts.length ? `${tab.label}: ${parts.join(", ")}` : null;
    })
    .filter(Boolean)
    .join("\n");

/**
 * State for the campaign assistant: the chat panel that builds a campaign from a description
 * instead of the recruiter assembling it node by node.
 *
 * The important part is applyToCanvas(). The assistant never sends a diagram. It sends the same
 * ArrayStructure and TriggerList the builder already stores, and the existing
 * loadAutomationIntoCanvas() draws it, so a campaign built by chat is drawn by exactly the same
 * code as a campaign reopened from a draft. There is no second renderer to keep in step.
 */
const campaignAiStore = observable({
  isOpen: false,
  // A reply arrived while the panel was hidden. Cleared the moment it is shown again.
  hasUnread: false,
  isSending: false,
  isSaving: false,
  sessionId: null,
  messages: [],
  plan: null,
  savedAutomationId: null,
  // The canvas as the assistant last drew it (editKey), to tell whether a new draft has been changed
  // by hand since, which decides how the panel's Save saves it. Cleared once a turn has sent the
  // server anything else, since the server may then hold a plan read from that instead.
  lastAppliedSnapshot: null,
  // The canvas as it was sent with the turn now running (editKey, DURING_TURN), to tell whether the
  // recruiter changed it while Roi was working.
  sentSnapshot: null,
  // Roi's version is on the canvas and has not been saved since (ROI-412). The builder's own
  // isAutomationFormDirty cannot say so: clicking a step, Finalise and coming back to the Builder tab
  // all clear it. Arms the leave prompt and the panel's Save changes.
  hasUnsavedDrawing: false,
  // From the latest response: the canvas ids of the steps its question card is about, and the steps
  // that still need action after a build ({ stepId, stepKey, label }). Both are marked on the canvas
  // (canvasMarks.js) and replaced by every response; empty when a response has none.
  focusStepIds: [],
  actionSteps: [],
  // From the latest response: the parts of the canvas Roi could not read ({ part, stepId, where,
  // what }), so it leaves the canvas alone (ROI-412). Shown above the message box and marked on the
  // canvas; replaced by every response.
  canvasIssues: [],
  // The question card Roi has open (ROI-417), answered with sendAnswer(); null when it asks nothing.
  card: null,
  // From the latest response: Worth a check ({ id, text, tabId }, Change reopens tabId with
  // requestChange()), Before you activate ({ stepId, stepKey, label, done }, Go to step with
  // goToStep()), the cells drawn ahead of an answer, and whether Finalise is offered (openFinalise()).
  worthACheck: [],
  beforeYouActivate: [],
  pendingStepIds: [],
  // The canvas shows Roi's outline rather than a built campaign.
  isOutline: false,
  finaliseReady: false,
  // 2: the panel shows Worth a check and Before you activate as their own blocks, so Roi's reply leaves
  // them out (Chris, Loom of 21 Oct 2026). 1 keeps them in the reply, for a panel that shows only the text.
  panelVersion: 2,
  // A step saved while a turn ran ({ stepId }), checked once the turn ends: a reply that does not build
  // keeps the list it had, so the save would otherwise go unchecked (Lesky's review).
  pendingStepSave: null,
  // Whether the server has the assistant switched on for this company. Off until it says so, so a
  // company outside the allow-list never sees the assistant. Not cleared by reset(): it does not
  // change between campaigns.
  isAvailable: false,
  hasCheckedAvailability: false,
  // Moves on whenever the conversation is reset or cleared, so a reply still on its way to the one
  // that was dropped can tell and is not shown in, or drawn by, the next (ROI-412).
  conversationVersion: 0,

  setIsOpen(isOpen) {
    this.isOpen = isOpen;
    if (isOpen) this.hasUnread = false;
  },

  reset() {
    this.isOpen = false;
    this.hasUnread = false;
    this.isSending = false;
    this.isSaving = false;
    this.sessionId = null;
    this.messages = [];
    this.plan = null;
    this.savedAutomationId = null;
    this.lastAppliedSnapshot = null;
    this.sentSnapshot = null;
    this.hasUnsavedDrawing = false;
    this.focusStepIds = [];
    this.actionSteps = [];
    this.canvasIssues = [];
    this.clearPanelLists();
    this.conversationVersion += 1;
  },

  clearPanelLists() {
    this.card = null;
    this.worthACheck = [];
    this.beforeYouActivate = [];
    this.pendingStepIds = [];
    this.isOutline = false;
    this.finaliseReady = false;
    this.pendingStepSave = null;
  },

  // The saved draft the builder has open, or null for a campaign not saved yet. A template being
  // used holds the template's id, which is not a campaign, so it counts as not saved.
  savedDraftId() {
    return automationStore.isUseAutomationTemplate ? null : automationStore.automationId || null;
  },

  /**
   * The campaign as the builder would save it, sent with every message so Roi starts from the
   * canvas as the recruiter left it, hand edits included (ROI-412). Built by the builder's own save
   * serialisers without their side effects. The trigger list is not flattened to 13 here, so a row
   * added or edited since the campaign was last loaded or saved keeps its own column. A row loaded
   * from a save already holds 13 for a custom field above the named triggers; its column is at the
   * front of customFieldTriggerId, which is what the back end reads. Null when there is no canvas.
   */
  canvasSnapshot() {
    if (!rappidStore.graph) return null;
    return {
      ...pick(automationStore.buildAutomationPayload(true), CANVAS_FIELDS),
      triggerType: automationStore.triggerType,
      arrayStructure: BuildArrayStructure(),
      triggerList: BuildTriggerList({ flattenCustomFields: false }),
    };
  },

  // editKey() of the canvas now; undefined when it cannot be read, which no snapshot matches.
  currentEditKey(options) {
    try {
      return editKey(this.canvasSnapshot(), options);
    } catch (e) {
      return undefined;
    }
  },

  sendMessage: action(function (text) {
    const message = (text || "").trim();
    if (!message) return Promise.resolve();
    return this.sendTurn({ message }, message);
  }),

  /**
   * Answers the open card (ROI-417): tabs is [{ tabId, optionIds, text }]. The server writes the message
   * from it; the conversation shows the same lines at once. A card that is no longer the open one is
   * not sent.
   */
  sendAnswer: action(function (cardId, tabs) {
    if (!this.card || this.card.id !== cardId) return Promise.reject(new Error("not the open card"));
    const shown = formatAnswer(this.card, tabs);
    if (!shown) return Promise.resolve();
    return this.sendTurn({ answer: { cardId, tabs } }, shown, { answer: { cardId, tabs } });
  }),

  // Change on a Worth a check line, or Edit on an answered card: Roi asks that one question again.
  requestChange: action(function (tabId) {
    if (!CHANGE_TAB_LABELS[tabId]) return Promise.resolve();
    return this.sendTurn({ change: { tabId } }, `Change: ${CHANGE_TAB_LABELS[tabId]}`, { change: { tabId } });
  }),

  sendTurn: action(function (input, shown, extra) {
    const message = shown;
    if (!message || this.isSending) return Promise.resolve();

    // Roi reads the canvas as saved, which leaves out a step form's unsaved changes, and a redraw
    // would close the form and lose them. Rejected, so the panel keeps what was typed.
    if (automationStore.isAutomationSidebarFormDirty.isDirty) {
      Alert.warning("Save or close the step you are editing first, so Roi can see it.");
      return Promise.reject(new Error("step form open"));
    }

    let canvas;
    let sentSnapshot;
    let isAsRoiDrewIt;
    try {
      canvas = this.canvasSnapshot();
      sentSnapshot = editKey(canvas, DURING_TURN);
      isAsRoiDrewIt = editKey(canvas) === this.lastAppliedSnapshot;
    } catch (e) {
      Alert.error("Roi could not read the campaign on the canvas. Please try again.");
      return Promise.reject(e);
    }

    this.messages.push({ role: "user", content: message, ...(extra || {}) });
    // The array holds its own observable copy, so that is the one to find again.
    const sent = this.messages[this.messages.length - 1];
    this.isSending = true;
    this.sentSnapshot = sentSnapshot;
    // When the canvas sent is not what Roi drew, the server may keep the plan it reads from it even if
    // this turn draws nothing. Its plan may then differ from the screen, so only the builder's save
    // may save the campaign from here on.
    if (!isAsRoiDrewIt) this.lastAppliedSnapshot = null;
    // The recruiter can leave the page, open another campaign or switch builder while a turn runs.
    // Its reply then belongs to a conversation that is gone, and is dropped.
    const conversationVersion = this.conversationVersion;
    const isStale = () => this.conversationVersion !== conversationVersion;

    // ApiService.postWithSignal rather than ApiService.post, purely so the global loading overlay
    // stays down. post() raises it unconditionally, and a turn here runs 30 to 50 seconds, so the
    // whole builder was locked behind a spinner for the entire time the recruiter was waiting: they
    // could not look at the canvas, scroll the conversation, or press Escape. The panel shows its own
    // progress instead, next to the message it belongs to. postWithSignal also rejects with the whole
    // error, so the HTTP status of a refusal can be read (ROI-412); add() passes on only its body.
    return ApiService.postWithSignal(
      ApiUrl.campaignAiChat,
      {
        sessionId: this.sessionId,
        ...input,
        panelVersion: this.panelVersion,
        automationType: automationStore.automationType,
        // The saved draft this turn is on, so the server can check it is still a draft and the
        // conversation's own, and the canvas as the recruiter left it (ROI-412).
        automationId: this.savedDraftId(),
        canvas,
      }
    )
      .catch(error => {
        // There is no message from ApiService here, so the turn says why it failed: in the server's
        // own words when it turned the turn down, else that Roi could not be reached.
        Alert.error(refusalMessage(error) || UNREACHABLE);
        return Promise.reject(error);
      })
      .then(
        action(response => {
          const result = response && response.data && response.data.result;
          if (!result || isStale()) return;

          this.sessionId = result.sessionId;
          // A different campaign (ROI-417): nothing of the old one is kept.
          this.plan = result.startedOver ? (result.arrayStructure && result.plan) || null : result.plan || this.plan;
          this.focusStepIds = readFocusStepIds(result);
          this.canvasIssues = readCanvasIssues(result);
          this.card = readCard(result);
          this.worthACheck = readWorthACheck(result);
          this.beforeYouActivate = readBeforeYouActivate(result);
          this.pendingStepIds = readPendingStepIds(result);
          // An outline stays the outline until something else is drawn over it.
          if (result.arrayStructure || result.startedOver) this.isOutline = !!result.outline;
          this.finaliseReady = !!result.finaliseReady;
          // The server keeps Before you activate from the last build on a turn that does not build, so the
          // marks no longer drop off between turns; actionSteps itself is only sent with a build.
          this.actionSteps = Array.isArray(result.actionSteps) ? readActionSteps(result) : stillToDo(this.beforeYouActivate);
          this.messages.push({
            role: "assistant",
            content: result.reply,
            toolsUsed: result.toolsUsed || [],
            replyOptions: result.replyOptions || [],
            // Several of the options can be picked together, such as the folders a campaign
            // draws from.
            replyOptionsMultiple: !!result.replyOptionsMultiple,
            // What the assistant asked this turn, shown as a card to step through.
            questions: result.questions || [],
            card: this.card,
            worthACheck: this.worthACheck,
          });

          // A turn can take tens of seconds, so the recruiter may well have hidden the panel and
          // gone back to the canvas by the time the reply lands.
          if (!this.isOpen) this.hasUnread = true;

          if (result.startedOver) {
            // The old campaign goes, Finalise settings included, before the new one is drawn; with no new
            // one yet, the canvas is left empty while Roi asks about it.
            if (result.arrayStructure) {
              const plan = { ...(result.plan || {}), finalise: (result.plan && result.plan.finalise) || DEFAULT_FINALISE };
              this.applyToCanvas(result.arrayStructure, result.triggerList, plan);
            } else {
              this.applyToCanvas("[]", "[]", {
                finalise: DEFAULT_FINALISE, audienceIds: [], segmentUserIds: [], segmentGroupIds: [], segmentOfficeIds: []
              });
            }
          } else if (result.arrayStructure) {
            this.applyToCanvas(result.arrayStructure, result.triggerList, result.plan);
          }
        })
      )
      .catch(
        action(error => {
          if (isStale()) return;
          // The turn failed, so take the message back out rather than leave it with no reply. The
          // panel puts the text back in the box, and a question card from the turn before shows again.
          // By reference: a "step is done" line may have been added after it meanwhile.
          const at = this.messages.indexOf(sent);
          if (at >= 0) this.messages.splice(at, 1);
          // An answer to a card that has moved on (409): the conversation is shown as the server has it,
          // with its latest card, rather than leaving the old card to be answered again and again.
          if (input && input.answer && error && error.response && error.response.status === 409) this.reloadSession();
          return Promise.reject(error);
        })
      )
      .finally(
        action(() => {
          // A turn sent in the next conversation may be running by now; it is not this reply's to end.
          if (isStale()) return;
          this.isSending = false;
          if (this.pendingStepSave) {
            const { stepId, pendingId } = this.pendingStepSave;
            this.pendingStepSave = null;
            this.stepSaved(stepId, pendingId);
          }
        })
      );
  }),

  /**
   * Draws the assistant's campaign using the builder's own loader, then captures the resulting
   * canvas so a save writes a real DiagramStructure rather than an empty scene.
   */
  applyToCanvas: action(function (arrayStructure, triggerList, plan) {
    if (!rappidStore.graph) return;

    // Roi built on the canvas as it was sent, so only a change made while it was working is missing
    // from its version (ROI-412), and drawing over it would lose it. A step form with unsaved changes
    // counts too: they are not on the canvas yet, and the redraw closes the form. Ask first; if they
    // keep theirs, the canvas is left alone, and is no longer what Roi last drew.
    const isChangedSinceSent = this.sentSnapshot
      && (automationStore.isAutomationSidebarFormDirty.isDirty || this.currentEditKey(DURING_TURN) !== this.sentSnapshot);
    if (isChangedSinceSent
        && !window.confirm("You changed the campaign while Roi was working. Replace those changes with Roi's version?")) {
      this.lastAppliedSnapshot = null;
      this.messages.push({
        role: "assistant",
        content: "I have left your changes on the canvas. My version is not drawn; ask me to redraw it if you want it.",
      });
      return;
    }

    // A step form left open would edit a node that is about to be replaced.
    automationStore.resetSidebar();

    // The builder's Save draft button is disabled while the name field is empty, so a campaign the
    // assistant drafted could not be saved until the recruiter typed a name themselves. The plan
    // already carries one derived from what they asked for, so put it in the field.
    if (plan && plan.name) automationStore.setAutomationName(plan.name);
    if (plan && plan.description) automationStore.setAutomationDescription(plan.description);

    // The Finalise settings the assistant chose from the campaign's purpose (Chris, 14 Oct 2026: it
    // should set indefinite and the category, not leave them for the recruiter). Indefinite first,
    // since switching it off resets looping; each switch resets its own period, so the period comes
    // after it. Re-entry's switch is inverted: restriction true means re-entry No.
    if (plan && plan.finalise) {
      const finalise = plan.finalise;
      automationStore.setIsAutomationIndefinite(!!finalise.keepRunning);
      automationStore.setLoopingType(
        finalise.looping ? AutomationLoopingType.Looping : AutomationLoopingType.ReEntryRestriction
      );
      automationStore.setIsAutomationReEntryRestriction(!finalise.reEntry);
      if (finalise.reEntry) {
        automationStore.setAutomationReEntryRestrictionWeek(finalise.reEntryWeeks || 0);
        automationStore.setAutomationReEntryRestrictionDay(finalise.reEntryDays || 0);
        automationStore.setAutomationReEntryRestrictionHour(finalise.reEntryHours || 0);
      }
      automationStore.setIsAutomationLooping(!!finalise.looping);
      if (finalise.looping) {
        automationStore.setAutomationLoopingWeek(finalise.loopingWeeks || 0);
        automationStore.setAutomationLoopingDay(finalise.loopingDays || 0);
        automationStore.setAutomationLoopingHour(finalise.loopingHours || 0);
      }

      // The campaign's own type stays first and fixed, as the builder keeps it.
      const typeTag =
        automationStore.automationType === AutomationType.Client ? AutomationTypeText.Contact : AutomationTypeText.Candidate;
      const tags = (finalise.tags || []).filter(tag => tag && tag.toLowerCase() !== typeTag.toLowerCase());
      automationStore.setTagOptions([
        { label: typeTag, value: typeTag, isFixed: true },
        ...tags.map(tag => ({ label: tag, value: tag })),
      ]);
    }

    // Audience and segment are campaign settings rather than canvas nodes, so the loader below
    // never sets them. Each is held twice: the selected* copy is what the trigger sidebar shows,
    // and the plain copy is what the builder's own Save draft and Finalise send. Setting only the
    // first drew the audience and then saved the campaign with none (automation 90525).
    if (plan) {
      const audienceIds = plan.audienceIds || [];
      const userIds = plan.segmentUserIds || [];
      const groupIds = plan.segmentGroupIds || [];
      const officeIds = plan.segmentOfficeIds || [];
      const includeRecentNoteOwner = !!plan.includeRecentNoteOwner;

      automationStore.setAutomationAudiencesIds([...audienceIds]);
      automationStore.setSelectedAutomationAudienceIds([...audienceIds]);
      automationStore.setAutomationUserIds([...userIds]);
      automationStore.setSelectedAutomationUserIds([...userIds]);
      automationStore.setAutomationGroupIds([...groupIds]);
      automationStore.setSelectedAutomationGroupIds([...groupIds]);
      automationStore.setAutomationOfficeIds([...officeIds]);
      automationStore.setSelectedAutomationOfficeIds([...officeIds]);
      automationStore.setIncludeRecentNoteOwner(includeRecentNoteOwner);
      automationStore.setSelectedIncludeRecentNoteOwner(includeRecentNoteOwner);
    }

    automationStore.setAutomationArrayStructure(arrayStructure);

    // Mirrors what automationStore does when it loads a saved campaign: set the trigger form, then
    // set the trigger type, from the plan when it says, else from the first column id. Skipping this
    // leaves the sidebar showing the wrong trigger family even though the canvas looks right.
    const triggers = triggerList ? JSON.parse(triggerList) : [];
    // Held twice as well: formTrigger is what the trigger sidebar edits, triggerList is what the
    // builder's own Save draft and Finalise send (ConvertTriggerListForBackEnd). Setting only the
    // first drew the triggers and saved the campaign with none (automations 90525, 90526, 90528,
    // all saved from the builder; 90527, saved from the assistant, kept them).
    automationStore.setTriggerList(triggers);
    if (triggers.length === 0) {
      // A one-off send to a folder has no trigger. Cleared rather than left alone, or a redraw of a
      // campaign that had one keeps showing it in the trigger form.
      automationStore.resetFormTrigger();
    } else {
      // Initialisation, as setAutomationData does: otherwise the form is marked dirty and the
      // builder's own Save draft and Finalise stay disabled on an assistant-drawn campaign.
      automationStore.setFormTrigger(triggers, true);
      const planTriggerType = plan && PlanTriggerTypes.get(String(plan.triggerType || "").toLowerCase());
      automationStore.setTriggerType(
        planTriggerType ||
        automationStore.setTriggerTypeByFirstColumnId(
          triggers[0].firstColumn.id,
          automationStore.automationType,
          triggers[0].triggerNumber || 1
        )
      );
    }

    loadAutomationIntoCanvas(arrayStructure, triggerList, automationStore.automationState, true);

    rappidStore.setCanvasJson(JSON.stringify(rappidStore.graph.toJSON()));
    automationStore.setAutomationDiagramStructure(rappidStore.canvasJson);
    automationStore.setAutomationSidebarFormToClean();
    // Roi's version is not saved yet, so leaving the page asks first.
    automationStore.setIsAutomationFormDirty(true);
    this.hasUnsavedDrawing = true;
    this.lastAppliedSnapshot = this.currentEditKey();
  }),

  /**
   * Go to step (ROI-417): selects the step on the canvas and opens its Edit panel next to Roi, as a
   * click on it does. False when the step is no longer on the canvas.
   */
  goToStep: action(function (stepId) {
    const graph = rappidStore.graph;
    const paper = rappidStore.paper;
    const cell = graph && stepId ? graph.getCell(stepId) : null;
    const view = cell && paper ? paper.findViewByModel(cell) : null;
    if (!view) {
      Alert.warning("That step is no longer on the canvas.");
      return false;
    }
    paper.trigger("element:pointerclick", view, { stopPropagation() {}, target: null }, 0, 0);
    return true;
  }),

  // Finalise in the chat (ROI-417): the Finalise tab, as the builder's own Finalise button opens it, and
  // not while that button is disabled, so a step form's unsaved changes are never left out (Lesky's review).
  openFinalise: action(function () {
    if (automationStore.isAutomationSidebarFormDirty.isDirty) {
      Alert.warning("Save or close the step you are editing first.");
      return false;
    }
    if (!automationStore.isTemplateValid) return false;
    automationStore.setAutomationTab(HeaderTabName.TAB_AUTOMATION_FINALISE);
    rappidStore.setCanvasJson(BuildCanvasJson());
    automationStore.setIsAutomationFormDirty(false);
    return true;
  }),

  // The waiting id a saved step clears: its own, or "trigger" for the trigger, the canvas's first cell.
  waitingIdFor(stepId) {
    if (!stepId) return null;
    if (this.pendingStepIds.includes(stepId)) return stepId;
    const trigger = rappidStore.graph && rappidStore.graph.getFirstCell && rappidStore.graph.getFirstCell();
    return trigger && trigger.id === stepId && this.pendingStepIds.includes("trigger") ? "trigger" : null;
  },

  /**
   * A step saved in the Edit panel (ROI-417): the server reads the canvas, ticks off what is now done in
   * Before you activate and says what is left. Quiet when it fails: the list catches up on the next turn.
   *
   * A waiting outline step filled in by hand stops waiting at once, so it does not hold Activate back,
   * and the server is told so a reopened draft or the next turn does not bring it back.
   */
  stepSaved: action(function (stepId, pendingId = this.waitingIdFor(stepId)) {
    if (pendingId) this.pendingStepIds = this.pendingStepIds.filter(id => id !== pendingId);
    if (!this.sessionId || (!pendingId && !this.beforeYouActivate.some(item => !item.done))) return Promise.resolve();
    // Not while a turn runs, which would race on the same reply: checked once the turn ends instead.
    if (this.isSending) {
      this.pendingStepSave = { stepId, pendingId };
      return Promise.resolve();
    }

    let canvas;
    try {
      canvas = this.canvasSnapshot();
    } catch (e) {
      return Promise.resolve();
    }
    if (!canvas) return Promise.resolve();

    const conversationVersion = this.conversationVersion;
    return ApiService.postWithSignal(ApiUrl.campaignAiStepSaved, { sessionId: this.sessionId, stepId, pendingStepId: pendingId, canvas })
      .then(
        action(response => {
          const result = response && response.data && response.data.result;
          if (!result || this.conversationVersion !== conversationVersion) return;

          this.beforeYouActivate = readBeforeYouActivate(result);
          this.finaliseReady = !!result.finaliseReady;
          this.actionSteps = stillToDo(this.beforeYouActivate);
          if (pendingId && Array.isArray(result.pendingStepIds)) this.pendingStepIds = readPendingStepIds(result);
          if (result.reply) {
            // Shown in the conversation; not sent to Roi, which reads the canvas itself next turn.
            this.messages.push({ role: "assistant", content: result.reply, local: true });
            if (!this.isOpen) this.hasUnread = true;
          }
        })
      )
      .catch(() => {});
  }),

  // The recruiter has changed a step Roi said needed action, so it is no longer marked as needing it.
  dismissActionStep: action(function (stepId) {
    this.actionSteps = this.actionSteps.filter(step => step.stepId !== stepId);
  }),

  /**
   * Saves the campaign from the panel.
   *
   * The server saves Roi's plan, not the canvas, so it only saves a new draft that is exactly as Roi
   * drew it. A saved draft, or a new one changed by hand since, is saved by the builder's own Save
   * draft, which saves what is on screen and updates a saved draft rather than copying it (ROI-412).
   *
   * When the server saves it, the builder is then brought in line with it the same way its own Save
   * draft does: the saved campaign is loaded into the store without redrawing, and the address moves
   * to it. With the id in the store, a later save updates this campaign rather than creating a
   * second one.
   */
  saveDraft: action(function () {
    if (!this.sessionId || this.isSaving) return Promise.resolve();

    const isSavedDraft = Boolean(this.savedDraftId());
    // A new draft is only saved here once Roi has built one. One the server has saved already, whose
    // details did not reach the builder, is left alone: the builder would create it a second time.
    if (!isSavedDraft && (this.savedAutomationId || !this.plan)) return Promise.resolve();

    const isAsRoiDrewIt = this.lastAppliedSnapshot && this.currentEditKey() === this.lastAppliedSnapshot;
    // The server has no plan to save for an outline, so the builder saves the canvas.
    if (isSavedDraft || !isAsRoiDrewIt || this.isOutline) return this.saveFromBuilder();

    this.isSaving = true;

    // Same reasoning as sendMessage: the button shows its own state.
    return ApiService.add(
      ApiUrl.campaignAiSaveDraft,
      {
        sessionId: this.sessionId,
        // The canvas is the authority on layout, so the diagram it produced goes with the save.
        diagramStructure: rappidStore.graph ? JSON.stringify(rappidStore.graph.toJSON()) : null,
      },
      "",
      "The campaign could not be saved",
      false
    )
      .then(
        action(response => {
          const result = response && response.data && response.data.result;
          if (!result || !result.automationId) return;

          // Set before the builder learns the id, so the automationId reaction reads it as this
          // conversation's own campaign rather than a different one to switch to.
          this.savedAutomationId = result.automationId;
          this.hasUnsavedDrawing = false;
          this.messages.push({ role: "assistant", content: result.reply });

          return ApiService.getDetail(ApiUrl.getAutomationDetail, result.automationId).then(
            action(detail => {
              if (detail && detail.status === 200 && detail.data && !detail.data.isError) {
                // As automationStore.save() does after the builder's own Save draft.
                automationStore.setAutomationData(detail, false, false);
                automationStore.setIsAutomationFormDirty(false);
              }

              window.history.pushState("", "", automationStore.automationType === AutomationType.Client
                ? `/admin/automation-client/story/${result.automationId}`
                : `/admin/automation/story/${result.automationId}`);
              Alert.success("Campaign saved as a draft");
            })
          );
        })
      )
      .finally(
        action(() => {
          this.isSaving = false;
        })
      );
  }),

  // The panel's Save through the builder's own Save draft. Not while a step form has unsaved
  // changes, as the builder's own button is not: the save closes the form.
  saveFromBuilder: action(function () {
    if (automationStore.isAutomationSidebarFormDirty.isDirty) return Promise.resolve();

    this.isSaving = true;
    return automationStore
      .saveDraftFromBuilder()
      .catch(() => {
        Alert.error("The campaign could not be saved");
      })
      .finally(
        action(() => {
          this.isSaving = false;
        })
      );
  }),

  /**
   * Ties the open conversation to the campaign the builder just saved it as. The builder's Save
   * draft and Finalise do not know about the chat, so without this every campaign saved from them
   * lost its conversation.
   */
  linkToAutomation: action(function (automationId) {
    if (!this.sessionId || !automationId) return Promise.resolve();

    this.savedAutomationId = automationId;
    return ApiService.add(
      ApiUrl.campaignAiLinkSession,
      { sessionId: this.sessionId, automationId },
      "",
      "",
      false
    ).catch(() => {
      // Not worth interrupting the recruiter over: the campaign is saved either way, and the next
      // save of the same campaign tries again.
      this.savedAutomationId = null;
    });
  }),

  // Forgets the conversation in the panel, for when a different campaign is opened.
  clearConversation: action(function () {
    this.sessionId = null;
    this.messages = [];
    this.plan = null;
    this.savedAutomationId = null;
    this.lastAppliedSnapshot = null;
    this.sentSnapshot = null;
    this.hasUnsavedDrawing = false;
    this.focusStepIds = [];
    this.actionSteps = [];
    this.canvasIssues = [];
    this.clearPanelLists();
    // A turn still running belongs to the conversation just dropped, so its reply is ignored and
    // nothing is being sent any more.
    this.isSending = false;
    this.conversationVersion += 1;
  }),

  // The trailing false on these reads keeps the global loading overlay alone: the builder raises it
  // while the campaign loads, and a quick read here would drop it early.
  loadAvailability: action(function () {
    if (this.hasCheckedAvailability) return Promise.resolve();
    this.hasCheckedAvailability = true;

    return ApiService.get(ApiUrl.campaignAiAvailability, "", "", false).then(
      action(response => {
        const result = response && response.data && response.data.result;
        this.isAvailable = Boolean(result && result.enabled);
      })
    ).catch(action(() => {
      // Asked again on the next visit to the builder rather than staying hidden for the session.
      this.hasCheckedAvailability = false;
    }));
  }),

  /**
   * Shows the conversation that built a campaign when the campaign is opened. The canvas already
   * has the saved campaign, which may have been edited by hand since, so this only restores the
   * chat and never redraws. Most campaigns were built by hand and have none.
   */
  loadForAutomation: action(function (automationId) {
    if (!automationId) return Promise.resolve();

    return ApiService.get(ApiUrl.campaignAiSessionForAutomation + automationId, "", "", false).then(
      action(response => {
        const result = response && response.data && response.data.result;
        if (!result || automationStore.automationId !== automationId) return;

        this.restore(result);
      })
    ).catch(() => {});
  }),

  // The open conversation as the server has it, such as after an answer to a card that had moved on.
  reloadSession: action(function () {
    const sessionId = this.sessionId;
    if (!sessionId) return Promise.resolve();
    const conversationVersion = this.conversationVersion;

    return ApiService.get(ApiUrl.campaignAiSession + sessionId, "", "", false).then(
      action(response => {
        const result = response && response.data && response.data.result;
        if (!result || this.conversationVersion !== conversationVersion || this.sessionId !== sessionId) return;
        this.restore(result);
      })
    ).catch(() => {});
  }),

  restore: action(function (result) {
    this.sessionId = result.sessionId;
    this.plan = result.plan;
    this.messages = result.messages || [];
    this.savedAutomationId = result.automationId;

    // As it was left (ROI-417): the lists from Roi's last reply, and its card if nothing came after it.
    const last = this.messages[this.messages.length - 1];
    this.card = last && last.role === "assistant" ? readCard(last) : null;
    this.worthACheck = readWorthACheck([...this.messages].reverse().find(m => m.role === "assistant" && m.worthACheck) || {});
    this.beforeYouActivate = readBeforeYouActivate(result);
    this.pendingStepIds = readPendingStepIds(result);
    this.finaliseReady = !!result.finaliseReady;
    this.actionSteps = stillToDo(this.beforeYouActivate);
  }),
});

// Follows the campaign the builder has open. A new id while an unsaved conversation is open is
// the builder saving it, so the two are linked; any other change of campaign starts the panel from
// that campaign's own conversation, if it has one. A first turn still running is a conversation
// too, with no session yet: the sessionId reaction below links it when the reply brings one.
reaction(
  () => automationStore.automationId,
  automationId => {
    if (!automationId) return;
    if (campaignAiStore.savedAutomationId === automationId) return;

    if ((campaignAiStore.sessionId || campaignAiStore.isSending) && !campaignAiStore.savedAutomationId) {
      if (campaignAiStore.sessionId) campaignAiStore.linkToAutomation(automationId);
      return;
    }

    campaignAiStore.clearConversation();
    campaignAiStore.loadForAutomation(automationId);
  }
);

// Any save of the campaign from the builder, as a draft, live or as a copy, saves Roi's version with it.
reaction(
  () => automationStore.saveCount,
  action(() => {
    campaignAiStore.hasUnsavedDrawing = false;
  })
);

// A conversation belongs to the builder it was started in, so moving between the candidate and contact
// builders starts the panel afresh rather than going on building a campaign of the other kind (ROI-412).
reaction(
  () => automationStore.automationType,
  () => campaignAiStore.clearConversation()
);

// A step saved in the Edit panel may be one Roi listed in Before you activate (ROI-417).
reaction(
  () => automationStore.stepSaveCount,
  () => campaignAiStore.stepSaved(automationStore.lastSavedStepId)
);

// A conversation started on a campaign that is already saved, and had none, belongs to it too.
reaction(
  () => campaignAiStore.sessionId,
  sessionId => {
    const automationId = automationStore.automationId;
    if (sessionId && automationId && !campaignAiStore.savedAutomationId) {
      campaignAiStore.linkToAutomation(automationId);
    }
  }
);

export default campaignAiStore;
